import { useQuery } from "@tanstack/react-query";
import {
  Building2,
  CalendarClock,
  GraduationCap,
  LineChart,
  MailQuestion,
  Search,
  TrendingUp,
  UserPlus,
  Users,
} from "lucide-react";
import * as React from "react";

import {
  fetchDealDashboard,
  getInsightsDashboard,
  getPartnerPerformance,
  getTrainingTeamReport,
  listManagerDeals,
  type DealDashboardMetrics,
  type InsightsDashboardMetrics,
  type InsightsPartnerPerformanceRow,
  type ManagerDealListItem,
  type TrainingTeamReportItem,
} from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { StatCard } from "@/components/StatCard";
import { InsightsGrowthDrawer } from "@/components/insights/InsightsGrowthDrawer";
import { NewPartnerNotifications } from "@/components/insights/NewPartnerNotifications";
import { PeriodSelect, type StatPeriod } from "@/components/insights/PeriodSelect";
import { PartnerDeepDiveDrawer } from "@/components/insights/PartnerDeepDiveDrawer";
import {
  PartnerPerformanceLedger,
  type PartnerPerformanceLedgerHandle,
} from "@/components/insights/PartnerPerformanceLedger";
import { InviteUserDialog } from "@/components/InviteUserDialog";
import {
  DealMetricDrawer,
  type DealMetricDataSource,
  type DealMetricMode,
} from "@/components/po-insights/DealMetricDrawer";
import { TeamTrainingDrawer } from "@/components/po-insights/TeamTrainingDrawer";
import { deriveCertification } from "@/components/training/certification";
import { Button } from "@/components/ui/button";
import { formatAud } from "@/lib/currency";
import { isExpiringWithin } from "@/lib/dealExpiry";
import { INVITE_CTA_LABEL } from "@/lib/role-labels";

const SEARCH_DEBOUNCE_MS = 300;

/** The "Expiring Deals" card count window. MUST match the DealMetricDrawer's
 * expiring mode — both count LIVE deals closing within this many days. */
const EXPIRING_WINDOW_DAYS = 30;

/** Which drawer is open. The page owns this; each drawer owns its body. A
 * DealMetricMode opens the program-wide deal drill-down (total-sales /
 * active-pipeline / expiring); "training" / "growth" open their own; null =
 * nothing open. */
type OpenDrawer = DealMetricMode | "training" | "growth" | null;

/** Program-wide source for the reused DealMetricDrawer — the manager dashboard
 * aggregate (/deals/dashboard, which is program-wide for CM/RM/RA and carries
 * the YTD won/lost fields) + the program manager deal feed (/deals/manager-list).
 * showPartnerColumn adds the leading Partner (org) column since this view spans
 * many orgs. Distinct cache scope from the PO org feed. */
const PROGRAM_DEAL_METRIC_SOURCE: DealMetricDataSource = {
  fetchMetrics: (token) => fetchDealDashboard(token),
  fetchDeals: (token) => listManagerDeals(token, "all"),
  queryKeyScope: "manager",
  showPartnerColumn: true,
};

/**
 * /dashboard/manager/insights — the Channel Partner Program Insights surface
 * for staff (CM / RM / RA). Program-wide for every staff role — no per-role
 * scope split (Wave-2 decision #1). SPIF/Rewards is CUT for v1, so there is no
 * rewards card or drawer.
 *
 * Mirrors the PO Insights shell MINUS Rewards (CHP review-r2 #5d029c6e):
 *  - a "Program overview" row of four KPI cards — Total sales closed YTD,
 *    Active pipeline, Expiring deals, Certification status — each a drill-down.
 *    The three deal cards open the reused DealMetricDrawer fed the program-wide
 *    source (/deals/dashboard + /deals/manager-list); the certification card
 *    opens the TeamTrainingDrawer's program-mode "Channel Partner Knowledge"
 *    cohort view.
 *  - a Growth-pulse hero line → the InsightsGrowthDrawer;
 *  - a Channel Partner Performance ledger whose rows open the PartnerDeepDiveDrawer.
 *
 * Route registration gates roles={["channel_manager","admin"]}; the backend
 * gates every /insights/* read on require_manager_or_admin.
 */
export function ManagerInsightsRoute() {
  const me = useCurrentUser();
  const [openDrawer, setOpenDrawer] = React.useState<OpenDrawer>(null);
  const [selectedPartnerId, setSelectedPartnerId] = React.useState<string | null>(null);

  if (me.isAuthLoading || me.isLoading) {
    return <p role="status">Loading…</p>;
  }
  if (!me.isAuthenticated || !me.data || !me.accessToken) {
    return <p role="alert">Sign in to view insights.</p>;
  }

  return (
    <InsightsContent
      token={me.accessToken}
      openDrawer={openDrawer}
      setOpenDrawer={setOpenDrawer}
      selectedPartnerId={selectedPartnerId}
      setSelectedPartnerId={setSelectedPartnerId}
    />
  );
}

interface InsightsContentProps {
  token: string;
  openDrawer: OpenDrawer;
  setOpenDrawer: (next: OpenDrawer) => void;
  selectedPartnerId: string | null;
  setSelectedPartnerId: (next: string | null) => void;
}

function InsightsContent({
  token,
  openDrawer,
  setOpenDrawer,
  selectedPartnerId,
  setSelectedPartnerId,
}: InsightsContentProps) {
  const dashboard = useQuery<InsightsDashboardMetrics>({
    queryKey: ["insights", "dashboard", token],
    queryFn: () => getInsightsDashboard(token),
    enabled: !!token,
  });
  const growth = dashboard.data?.growth;

  // Program Overview deal cards read the program-wide deal dashboard
  // (/deals/dashboard — program-scoped for CM/RM/RA, carrying the YTD won/lost
  // and active-deal fields the Insights dashboard endpoint lacks). The same
  // aggregate backs the DealMetricDrawer's sub-cards, so card ↔ modal can't
  // diverge. Mirrors the PO Insights surface.
  const dealDashboard = useQuery<DealDashboardMetrics>({
    queryKey: ["deals", "manager-dashboard", token],
    queryFn: () => fetchDealDashboard(token),
    enabled: !!token,
  });
  const metrics = dealDashboard.data;

  // The Expiring Deals card counts LIVE deals closing ≤30 days from the SAME
  // feed the Expiring modal uses, so the count can never drift from the drawer.
  const dealFeed = useQuery<ManagerDealListItem[]>({
    queryKey: ["deals", "manager-list", "all", token],
    queryFn: () => listManagerDeals(token, "all"),
    enabled: !!token,
  });
  const expiringCount = React.useMemo(() => {
    if (!dealFeed.data) return "—" as const;
    const now = Date.now();
    return dealFeed.data.filter((d) => isExpiringWithin(d, now, EXPIRING_WINDOW_DAYS)).length;
  }, [dealFeed.data]);

  // The Certification Status card shows the share of the channel cohort that is
  // fully certified — read from the program-wide training report, the same feed
  // the Channel Partner Knowledge modal breaks down by cohort.
  const trainingReport = useQuery<TrainingTeamReportItem[]>({
    queryKey: ["training", "program-report"],
    queryFn: () => getTrainingTeamReport(token),
    enabled: !!token,
  });
  const certifiedPct = React.useMemo(() => {
    const rows = trainingReport.data;
    if (!rows) return "—" as const;
    if (rows.length === 0) return "0%";
    const certified = rows.filter((r) => deriveCertification(r) === "certified").length;
    return `${Math.round((certified / rows.length) * 100)}%`;
  }, [trainingReport.data]);

  // Scroll-position restore for the non-modal drawer flows (deals drill-down +
  // partner deep-dive). The Insights page scrolls on the document, so capture
  // window.scrollY when any drawer opens and restore it once they all close
  // (named AC on the partner-deep-dive + program-deals drawer tickets).
  const dealsDrawerOpen =
    openDrawer === "total-sales" || openDrawer === "active-pipeline" || openDrawer === "expiring";
  const anyDrawerOpen = dealsDrawerOpen || selectedPartnerId !== null;
  const savedScrollY = React.useRef(0);
  const wasOpen = React.useRef(false);
  React.useEffect(() => {
    if (anyDrawerOpen && !wasOpen.current) {
      savedScrollY.current = window.scrollY;
      wasOpen.current = true;
    } else if (!anyDrawerOpen && wasOpen.current) {
      const y = savedScrollY.current;
      window.requestAnimationFrame(() => window.scrollTo(0, y));
      wasOpen.current = false;
    }
  }, [anyDrawerOpen]);

  // Channel Partner Performance ledger (CHP-782) — its own debounced search box
  // (server-side ?q= company-name substring) that drives the org-level
  // performance table.
  const [perfSearchInput, setPerfSearchInput] = React.useState("");
  const [perfDebounced, setPerfDebounced] = React.useState("");
  React.useEffect(() => {
    const t = window.setTimeout(() => setPerfDebounced(perfSearchInput), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [perfSearchInput]);
  const perfQuery = perfDebounced.trim();
  const performance = useQuery<InsightsPartnerPerformanceRow[]>({
    queryKey: ["insights", "partner-performance", perfQuery, token],
    queryFn: () => getPartnerPerformance(token, perfQuery === "" ? undefined : perfQuery),
    enabled: !!token,
  });

  // CHP review-r2 (#be80529b): "Add channel partner" CTA under the title. Reuses
  // the shared InviteUserDialog scoped to channel_partner invites (PO/BDM fan-out
  // handled by the dialog). Closing on success refreshes any partner-scoped lists.
  const [inviteOpen, setInviteOpen] = React.useState(false);

  // CHP-819: the header "Search channel partners" affordance scrolls to + focuses
  // the Channel Partner Performance ledger's own search box.
  const perfLedgerRef = React.useRef<PartnerPerformanceLedgerHandle>(null);

  // Program Overview card values (CHP review-r2 #5d029c6e). Monetary fields are
  // cents → whole-dollar via formatAudWhole.
  //
  // Total sales closed: closed-won value + count over the selected window
  // (calendar buckets per the CHP-814 rule decision). Mirrors the PO card,
  // including the CHP-841 period picker (month/quarter/year, seeded to year);
  // the drill-down modal adds the won/lost value+volume breakdown.
  const [closedPeriod, setClosedPeriod] = React.useState<StatPeriod>("year");
  const closedFigures = metrics
    ? {
        month: {
          value: metrics.closed_won_value_month,
          count: metrics.closed_won_count_month,
        },
        quarter: {
          value: metrics.closed_won_value_quarter,
          count: metrics.closed_won_count_quarter,
        },
        year: { value: metrics.closed_won_value_ytd, count: metrics.closed_won_count_ytd },
      }[closedPeriod]
    : null;
  const closedYtdValue = closedFigures ? formatAud(closedFigures.value) : "—";
  const closedYtdSubtitle = closedFigures
    ? `+${closedFigures.count} deal${closedFigures.count === 1 ? "" : "s"}`
    : undefined;
  // Active Pipeline: LIVE-deal value + live-deal volume ("# live"), matching the
  // modal's Total-Pipeline-Value (LIVE-only) sub-card.
  const pipelineValue = metrics ? formatAud(metrics.active_deals_value) : "—";
  // CHP-827: spell out "deals" after the live count.
  const pipelineSubtitle = metrics ? `${metrics.active_deals} live deals` : undefined;
  // Expiring Deals: # closing within 30 days (client-side from the feed).
  const expiringSubtitle =
    typeof expiringCount === "number" && expiringCount > 0 ? "closing ≤30 days" : undefined;

  return (
    <div className="container py-6">
      <header className="mb-4">
        {/* Product nav/page copy (RACCPE-17/18), not a role-vocabulary token. */}
        {/* eslint-disable-next-line no-restricted-syntax */}
        <h1 className="text-2xl font-semibold tracking-tight">
          Channel Partner Program, at a glance.
        </h1>
        {/* CHP-851: subtitle restored per designer re-review (overlaps CHP-988). */}
        <p className="mt-1 text-sm text-muted-foreground">
          Track program performance and engagement data to drive actionable insights.
        </p>
        {/* CHP review-r2 (#be80529b): "Add channel partner" CTA directly under
            the title. CHP-819: a "Search channel partners" affordance sits beside
            it and jumps to the performance ledger's search box. */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            onClick={() => setInviteOpen(true)}
            data-testid="insights-add-partner"
          >
            <UserPlus className="mr-2 h-4 w-4" aria-hidden />
            {INVITE_CTA_LABEL.partner_invite}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => perfLedgerRef.current?.focusSearch()}
            data-testid="insights-search-partners"
          >
            <Search className="mr-2 h-4 w-4" aria-hidden />
            Search channel partners
          </Button>
        </div>
      </header>

      {/* Explicit dashboard error state (AC #8): when GET /insights/dashboard
          fails, show a distinct error — not the "—" placeholders that also read
          as the loading state — mirroring the partner-search error branch. */}
      {dashboard.isError ? (
        <div
          role="alert"
          className="mt-6 rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive"
          data-testid="insights-dashboard-error"
        >
          Couldn&apos;t load the Insights dashboard. Please refresh to try again.
        </div>
      ) : null}

      {/* CHP-1193 (UAT AC-A-08): unread new-partner-onboarded notifications —
          the Flow A step-5 dashboard to-do. Renders nothing when none unread. */}
      <NewPartnerNotifications token={token} />

      {/* Program Overview (CHP review-r2 #5d029c6e) — four KPI cards, each a
          drill-down into the matching modal. Replaces the former Deals Tracking
          + Domain Performance rows. */}
      <section className="mt-8">
        <h2 className="text-lg font-semibold tracking-tight">Program overview</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* Total sales closed → Total Sales drill-down.
              CHP-841: in-card period picker (month/quarter/year, seeded year).
              testId keeps its historical "-ytd" suffix — asserted in tests. */}
          <StatCard
            title="Total sales closed"
            value={closedYtdValue}
            subtitle={closedYtdSubtitle}
            icon={TrendingUp}
            testId="insights-closed-ytd"
            // Genuine positive achievement — teal numeral.
            tone="success"
            onClick={() => setOpenDrawer("total-sales")}
            action={
              <PeriodSelect
                value={closedPeriod}
                onChange={setClosedPeriod}
                testId="insights-closed-period"
                ariaLabel="Sales closed period"
              />
            }
          />
          {/* Active Pipeline → Active Pipeline drill-down. */}
          <StatCard
            title="Active pipeline"
            value={pipelineValue}
            subtitle={pipelineSubtitle}
            icon={LineChart}
            testId="insights-pipeline"
            tone="neutral"
            onClick={() => setOpenDrawer("active-pipeline")}
          />
          {/* Expiring Deals → Expiring drill-down. */}
          <StatCard
            title="Expiring deals"
            value={expiringCount}
            subtitle={expiringSubtitle}
            icon={CalendarClock}
            testId="insights-expiring"
            // Attention metric — colour by exception (amber left edge) only
            // when deals are actually expiring.
            tone="neutral"
            accent={typeof expiringCount === "number" && expiringCount > 0 ? "amber" : undefined}
            onClick={() => setOpenDrawer("expiring")}
          />
          {/* Certification Status → Channel Partner Knowledge drill-down. */}
          <StatCard
            title="Certification status"
            value={certifiedPct}
            subtitle="program certified"
            icon={GraduationCap}
            testId="insights-training"
            tone="neutral"
            onClick={() => setOpenDrawer("training")}
          />
        </div>
      </section>

      {/* CHP-844/846: "Growth Pulse" → "Channel partner reach", and the single
          hero line is split into three KPI cards (Active Partners / Program
          Members / Pending Invitations). Each opens the reach directory (the
          former growth ledger). The +Nd-in-30d deltas move into that directory
          (CHP-848 period filter), so the cards show a neutral "—" rather than a
          misleading 0 before the dashboard loads (preserves CHP-809). */}
      <section className="mt-8">
        <h2 className="text-lg font-semibold tracking-tight">Channel partner reach</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard
            title="Active partners"
            value={growth ? growth.total_channel_partner_orgs : "—"}
            subtitle="channel partner organisations"
            icon={Building2}
            tone="neutral"
            testId="insights-reach-partners"
            onClick={() => setOpenDrawer("growth")}
          />
          <StatCard
            title="Program members"
            value={growth ? growth.total_individuals : "—"}
            subtitle="BDMs & Programme Owners"
            icon={Users}
            tone="neutral"
            testId="insights-reach-members"
            onClick={() => setOpenDrawer("growth")}
          />
          <StatCard
            title="Pending invitations"
            value={growth ? growth.invited_not_registered_count : "—"}
            subtitle="invited, not yet registered"
            icon={MailQuestion}
            tone="neutral"
            testId="insights-reach-pending"
            onClick={() => setOpenDrawer("growth")}
          />
        </div>
      </section>

      {/* Channel Partner Performance (CHP-782) — org-level ledger, one row per
          Channel-Partner organisation. Server-side ?q= search (300ms debounce)
          + a client-side Status filter (v1; full facets are a follow-up). */}
      <section className="mt-8">
        {/* Section heading copy (CHP-782); sentence-cased (CHP-980), matching the
            "Channel partner reach" sibling — lowercase, so not a role token. */}
        <h2 className="text-lg font-semibold tracking-tight">Channel partner performance</h2>
        {/* CHP-850: descriptive sub-line removed per designer review. */}
        <div className="mt-4" data-testid="partner-performance-section">
          {performance.isError ? (
            <div
              role="alert"
              className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive"
              data-testid="partner-performance-error"
            >
              Couldn&apos;t load channel partner performance. Please refresh to try again.
            </div>
          ) : (
            <PartnerPerformanceLedger
              ref={perfLedgerRef}
              rows={performance.data ?? []}
              searchValue={perfSearchInput}
              onSearchChange={setPerfSearchInput}
              // CHP-864: whole-row click opens the per-CP drill-down modal
              // (PartnerDeepDiveDrawer).
              onRowClick={setSelectedPartnerId}
            />
          )}
        </div>
      </section>

      {/* Drawers — the page owns open/close; each drawer owns its body. */}
      {/* Program Overview deal cards → the reused DealMetricDrawer fed the
          program-wide source (CHP review-r2 #c3b3cadc/#8bb4a3c0): Total Sales
          Closed YTD (won/lost value+volume), Active Pipeline, and Expiring
          (Total Risk Value / Deal Count / Urgency Index), each with the shared
          Advanced-Filters panel + status chips. */}
      <DealMetricDrawer
        mode={dealsDrawerOpen ? (openDrawer as DealMetricMode) : null}
        dataSource={PROGRAM_DEAL_METRIC_SOURCE}
        onClose={() => setOpenDrawer(null)}
      />
      {/* Certification Status card → Channel Partner Knowledge modal
          (CHP review-r2 #c02c2843): the spec title + supporting copy, and the
          program-mode 4-card % cohort split rendered inside the drawer. */}
      <TeamTrainingDrawer
        open={openDrawer === "training"}
        onClose={() => setOpenDrawer(null)}
        showPartnerColumn
        // Designer's verbatim modal title (CHP review-r2 #c02c2843) — product
        // copy, not a role-vocabulary token (same as the page H1 above).
        // eslint-disable-next-line no-restricted-syntax
        title="Channel Partner Knowledge"
        description="Manage and track organisation-wide training and development."
      />
      <InsightsGrowthDrawer open={openDrawer === "growth"} onClose={() => setOpenDrawer(null)} />
      <PartnerDeepDiveDrawer
        partnerId={selectedPartnerId}
        onClose={() => setSelectedPartnerId(null)}
      />

      {/* CHP review-r2 (#be80529b): Add channel partner. Scoped to
          channel_partner invites; the dialog fans out PO/BDM internally. */}
      <InviteUserDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        token={token}
        allowedRoles={["channel_partner"]}
        invalidateOnSuccess={[
          ["insights", "partners"],
          ["insights", "dashboard"],
          ["profiles", "manager-team"],
        ]}
      />
    </div>
  );
}
